import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import { IconButton } from "@mhome/ui";
import CloseIcon from "@mui/icons-material/Close";
import { debounce } from "lodash";
import { searchLocation } from "../service/scope-service";
import { useWebSocket } from "../ws/websocket";
import { spacingToPx } from "../lib/utils";

const LocationDialog = ({
  open,
  onClose,
  onSave,
  onDelete,
  initialData = null,
  locationNames = [],
  isHomeLocation = false,
}) => {
  const [locationType, setLocationType] = useState("other");
  const [locationName, setLocationName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const { sendAndWaitWs } = useWebSocket();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialData) {
      setLocationName(initialData.name);
      if (
        initialData.locationId &&
        initialData.locationId.toLowerCase() === "home"
      ) {
        setLocationType("home");
      } else {
        setLocationType("other");
      }
      setSelectedAddress({
        address: initialData.address,
        latitude: initialData.latitude,
        longitude: initialData.longitude,
        timezone: initialData.timezone,
        locationId: initialData.locationId,
      });
      setSearchQuery(initialData.address || "");
    } else {
      if (isHomeLocation) {
        setLocationType("home");
        setLocationName("Home");
      } else {
        setLocationType("other");
        setLocationName("");
      }
      setSelectedAddress(null);
      setSearchQuery("");
      setOptions([]);
    }
  }, [initialData, isHomeLocation, open]);

  const searchLocations = useCallback(
    async (query) => {
      if (!query || !query.trim()) {
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const resp = await searchLocation(sendAndWaitWs, query);
        let resultOptions = [];
        if (resp) {
          if (resp.error) {
            console.error("Search location error:", resp.error);
            resultOptions = [];
          } else if (Array.isArray(resp)) {
            resultOptions = resp;
          } else if (Array.isArray(resp.locations)) {
            resultOptions = resp.locations;
          } else if (Array.isArray(resp.results)) {
            resultOptions = resp.results;
          } else {
            console.warn("Unexpected response format:", resp);
            resultOptions = [];
          }
        }

        setOptions(resultOptions);
      } catch (error) {
        console.error("Error searching locations:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [sendAndWaitWs]
  );

  const debouncedSearch = useCallback(
    debounce((query) => {
      searchLocations(query);
    }, 500),
    [searchLocations]
  );

  const handleNameChange = (e) => {
    const value = e.target.value;
    setLocationName(value);

    if (value.trim().toLocaleLowerCase() === "home") {
      setError("Location name cannot be 'Home' for type 'Other'");
    } else if (locationNames.length > 0) {
      if (
        locationNames.some((name) => {
          return name.trim().toLowerCase() === value.trim().toLowerCase();
        })
      ) {
        setError("Location name already exists.");
      } else {
        setError("");
      }
    }
  };

  const handleSave = () => {
    setError("");
    if (!locationName || !selectedAddress) return;

    onSave({
      name: locationName,
      address: selectedAddress.address,
      latitude: selectedAddress.latitude,
      longitude: selectedAddress.longitude,
      timezone: selectedAddress.timezone,
      locationId:
        locationType === "home" ? "home" : selectedAddress.locationId || null,
    });
    onClose();
    setSelectedAddress(null);
  };

  const handleDelete = () => {
    if (initialData && onDelete) {
      onDelete(initialData);
      onClose();
    }
  };

  const handleDeleteClick = () => {
    if (locationType === "home") {
      setConfirmDeleteOpen(true);
    } else {
      handleDelete();
    }
  };

  const handleDeleteConfirm = () => {
    handleDelete();
    setConfirmDeleteOpen(false);
  };

  const getDialogTitle = () => {
    if (initialData) {
      return locationType === "home" ? "Edit Home Location" : "Edit Location";
    } else {
      return locationType === "home" ? "Add Home Location" : "Add Location";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        variant="pops"
        maxWidth="sm"
        PaperProps={{
          style: {
            width: "90%",
            maxWidth: "500px",
          },
        }}
      >
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacingToPx(2),
              marginTop: spacingToPx(2),
            }}
          >
            {locationType === "other" && (
              <>
                <TextField
                  value={locationName}
                  onChange={(e) => handleNameChange(e)}
                  fullWidth
                  required
                  error={!!error}
                  placeholder="Location Name"
                  helperText={error}
                  size="medium"
                  style={{
                    minHeight: "56px",
                    fontSize: "16px",
                  }}
                  InputProps={{
                    style: {
                      minHeight: "56px",
                      height: "56px",
                      fontSize: "16px",
                    },
                  }}
                />
              </>
            )}
            <Autocomplete
              freeSolo
              options={options || []}
              getOptionLabel={(option) => {
                if (!option) return "";
                if (typeof option === "string") return option;
                if (
                  typeof option === "object" &&
                  option.address &&
                  typeof option.address === "string"
                ) {
                  return option.address;
                }
                return "";
              }}
              value={selectedAddress || null}
              inputValue={typeof searchQuery === "string" ? searchQuery : ""}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue || typeof inputValue !== "string") {
                  return options;
                }
                const query = inputValue.toLowerCase().trim();
                if (!query) {
                  return options;
                }
                return options.filter((option) => {
                  if (typeof option === "string") {
                    return option.toLowerCase().includes(query);
                  }
                  if (
                    option &&
                    typeof option === "object" &&
                    option.address &&
                    typeof option.address === "string"
                  ) {
                    return option.address.toLowerCase().includes(query);
                  }
                  return false;
                });
              }}
              onInputChange={(event, newInputValue) => {
                let cleanValue = "";
                if (typeof newInputValue === "string") {
                  cleanValue = newInputValue;
                } else if (
                  newInputValue === null ||
                  newInputValue === undefined
                ) {
                  cleanValue = "";
                } else if (
                  typeof newInputValue === "object" &&
                  newInputValue.address
                ) {
                  cleanValue = String(newInputValue.address || "");
                } else {
                  cleanValue = "";
                }

                setSearchQuery(cleanValue);
                setSelectedAddress(null);

                const trimmedValue = cleanValue.trim();
                if (!trimmedValue) {
                  setLoading(false);
                  setOptions([]);
                  debouncedSearch.cancel();
                } else {
                  debouncedSearch(cleanValue);
                }
              }}
              onChange={(event, newValue) => {
                if (
                  newValue === null ||
                  newValue === undefined ||
                  newValue === "" ||
                  (typeof newValue === "object" && !newValue.address)
                ) {
                  setSelectedAddress(null);
                  setSearchQuery("");
                  setLoading(false);
                  return;
                }

                if (typeof newValue === "string") {
                  setSelectedAddress(null);
                  setSearchQuery(String(newValue));
                } else if (
                  newValue &&
                  typeof newValue === "object" &&
                  newValue.address &&
                  typeof newValue.address === "string"
                ) {
                  setSelectedAddress(newValue);
                  setSearchQuery(String(newValue.address));
                } else {
                  setSelectedAddress(null);
                  setSearchQuery("");
                }
                setLoading(false);
              }}
              loading={loading}
              componentsProps={{
                paper: {
                  style: {
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                    marginTop: "4px",
                  },
                },
              }}
              renderInput={(params) => {
                const hasInput =
                  searchQuery &&
                  typeof searchQuery === "string" &&
                  searchQuery.trim();
                const safeValue =
                  typeof searchQuery === "string" ? searchQuery : "";
                const originalEndAdornment = params.InputProps?.endAdornment;
                return (
                  <TextField
                    {...params}
                    placeholder="Search Address"
                    value={safeValue}
                    size="medium"
                    style={{
                      minHeight: "56px",
                      fontSize: "16px",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      style: {
                        ...params.InputProps?.style,
                        minHeight: "56px",
                        height: "56px",
                        fontSize: "16px",
                      },
                      endAdornment: (
                        <>
                          {loading && <CircularProgress size={20} />}
                          {hasInput ? (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setSelectedAddress(null);
                                setSearchQuery("");
                                setOptions([]);
                                setLoading(false);
                                debouncedSearch.cancel();
                              }}
                              style={{
                                padding: "4px",
                                marginRight: "4px",
                              }}
                            >
                              <CloseIcon style={{ fontSize: "18px" }} />
                            </IconButton>
                          ) : (
                            !loading && originalEndAdornment
                          )}
                        </>
                      ),
                    }}
                  />
                );
              }}
            />
          </div>
        </DialogContent>
        <DialogActions
          style={{
            paddingLeft: spacingToPx(1),
            paddingRight: spacingToPx(1),
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex" }}>
            {initialData && locationType !== "home" && (
              <Button
                onClick={handleDeleteClick}
                color="error"
                variant="outlined"
              >
                Delete
              </Button>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={
                !locationName ||
                !selectedAddress ||
                (locationType === "other" && !locationName.trim()) ||
                error
              }
            >
              Save
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        variant="pops"
      >
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          <div>
            Removing location &apos;Home&apos; will disable the time & schedule
            related capabilities. Do you want to continue?
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LocationDialog;
